import React, { useState, useEffect } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { isAuthenticated, refreshIfNeeded } from '../auth/okta';

type AuthRouteProps = {
  requireAuth: boolean;
  authenticatedRedirectTo?: string;
  unauthenticatedRedirectTo?: string;
};

/**
 * Route wrapper for both protected and guest-only route groups.
 * - requireAuth=true  => authenticated users pass, others redirected
 * - requireAuth=false => guests pass, authenticated users redirected
 *
 * When requireAuth=true and the access token is expired, a silent refresh
 * is attempted first (doRefresh includes the inactivity gate: if the user
 * was idle for more than 1 hour, logout() is called and they go to /login).
 */
const AuthRoute: React.FC<AuthRouteProps> = ({
  requireAuth,
  authenticatedRedirectTo = '/',
  unauthenticatedRedirectTo = '/login',
}) => {
  const location = useLocation();
  const [checked, setChecked] = useState(false);
  const [authed, setAuthed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    async function check() {
      if (requireAuth && !isAuthenticated()) {
        // Token is expired — try a silent refresh before deciding to redirect.
        // refreshIfNeeded → doRefresh → inactivity gate:
        //   active within 1 hr  → new token → isAuthenticated() = true
        //   idle > 1 hr         → logout() called → isAuthenticated() = false
        await refreshIfNeeded();
      }
      if (!cancelled) {
        setAuthed(isAuthenticated());
        setChecked(true);
      }
    }
    check();
    return () => { cancelled = true; };
  // Re-run when the path changes so navigating to a protected page
  // after the token expires also triggers the check.
  }, [location.pathname, requireAuth]);

  // Hold render until the async check completes to avoid a flash of the
  // wrong page (e.g. briefly showing /login before refresh succeeds).
  if (requireAuth && !checked) return null;

  if (requireAuth) {
    return authed
      ? <Outlet />
      : <Navigate
          to={`${unauthenticatedRedirectTo}?from=${encodeURIComponent(location.pathname + location.search)}`}
          replace
        />;
  }

  return authed
    ? <Navigate to={authenticatedRedirectTo} replace state={{ from: location }} />
    : <Outlet />;
};

export default AuthRoute;
